// Core
import { DateTime } from "luxon";
import moment from "moment";
// Utils
import { GRANULARITY_LEVELS } from "./constants/time";

/**
 * The user's browser locale (first preferred language), used for every
 * user-facing date so tables, pickers and labels share one format.
 */
export function browserLocale() {
  if (typeof navigator === "undefined") return "en-US";
  const candidate = (navigator.languages && navigator.languages[0]) || navigator.language;
  try {
    return (candidate && Intl.DateTimeFormat.supportedLocalesOf(candidate)[0]) || "en-US";
  } catch (e) {
    return "en-US";
  }
}

const DATE_FORMAT = { year: "numeric", month: "2-digit", day: "2-digit" };
const DAY_MONTH_FORMAT = { month: "2-digit", day: "2-digit" };

/**
 * Accepts an ISO date ("2026-10-15"), ISO datetime, SQL datetime, Date,
 * Luxon DateTime or epoch millis. Plain ISO dates are kept as calendar dates
 * (no timezone shift); datetimes keep the offset they were sent with.
 */
function toLuxon(value) {
  if (value == null || value === "") return null;
  if (DateTime.isDateTime(value)) return value.isValid ? value : null;
  if (value instanceof Date) {
    const dt = DateTime.fromJSDate(value);
    return dt.isValid ? dt : null;
  }
  if (typeof value === "number") {
    const dt = DateTime.fromMillis(value);
    return dt.isValid ? dt : null;
  }
  const text = String(value);
  let dt = DateTime.fromISO(text, { setZone: true });
  if (!dt.isValid) dt = DateTime.fromSQL(text, { setZone: true });
  if (!dt.isValid) dt = DateTime.fromJSDate(new Date(text));
  return dt.isValid ? dt : null;
}

/** Date in the browser locale, e.g. 15/10/2026 (pt-PT, en-GB) or 10/15/2026 (en-US). */
export function formatDate(value, fallback = "-") {
  const dt = toLuxon(value);
  if (!dt) return value ? String(value) : fallback;
  return dt.setLocale(browserLocale()).toLocaleString(DATE_FORMAT);
}

/** Day and month only in the browser locale order, plus 24h time (chart axes). */
export function formatDayMonthTime(value, fallback = "-") {
  const dt = toLuxon(value);
  if (!dt) return value ? String(value) : fallback;
  return `${dt.setLocale(browserLocale()).toLocaleString(DAY_MONTH_FORMAT)} ${dt.toFormat(
    "HH:mm"
  )}`;
}

/** Browser-locale date + 24h time, converted to the browser's timezone. */
export function formatLocalDateTime(value, fallback = "-") {
  const dt = toLuxon(value);
  if (!dt) return value ? String(value) : fallback;
  const local = dt.toLocal();
  return `${formatDate(local, fallback)} ${local.toFormat("HH:mm")}`;
}

/** Machine-readable ISO date (yyyy-MM-dd) for state, queries and sorting. */
export function datetimeToDate(datetime) {
  if (!datetime) return "-";
  const dt = DateTime.fromISO(datetime, { setZone: true });
  return dt.toFormat("yyyy-MM-dd");
}

export function datetimeToDateNoUTC(datetime) {
  return moment(datetime).format("YYYY-MM-DD");
}

export function datetimeToTime(datetime) {
  if (!datetime) return "-";
  const dt = DateTime.fromISO(datetime, { setZone: true });
  return dt.toFormat("HH:mm ZZZZ");
}

export function formatDateTime(datetime) {
  if (!datetime) return "-";
  const dt = DateTime.fromISO(datetime, { setZone: true });
  if (!dt.isValid) return String(datetime);
  return `${formatDate(dt)} ${dt.toFormat("HH:mm ZZZZ")}`;
}

export function today() {
  return DateTime.now().toUTC().toISO();
}

export function yesterday() {
  return DateTime.now().minus({ days: 1 }).toUTC().toISO();
}

export function tomorrow() {
  return DateTime.now().plus({ days: 1 }).toUTC().toISO();
}

export function getDateFromDatetime(datetime) {
  return new Date(datetime).getDate();
}

export function getMonthFromDatetime(datetime) {
  return new Date(datetime).getMonth();
}

export function getYearFromDatetime(datetime) {
  return new Date(datetime).getFullYear();
}

/**
 * Utility function to limit the granularity levels for performance reasons.
 */
export function getGranularityLevels(timeHorizonInDays) {
  if (timeHorizonInDays <= 1) {
    return GRANULARITY_LEVELS;
  }
  if (timeHorizonInDays <= 7) {
    return GRANULARITY_LEVELS.slice(1);
  }
  if (timeHorizonInDays <= 180) {
    return GRANULARITY_LEVELS.slice(2);
  }
  if (timeHorizonInDays <= 365) {
    return GRANULARITY_LEVELS.slice(3);
  }

  return GRANULARITY_LEVELS.slice(3);
}

/**
 * Default granularity for a date range: the finest level that keeps the point
 * count sensible for that span (≤1d→15min, ≤7d→hour, ≤180d→day, else month).
 * The backend always requires a granularity, so this is the implicit default
 * used everywhere instead of a manual selector.
 */
export function autoGranularity(startDate, endDate) {
  const days = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24);
  const safeDays = Number.isFinite(days) ? Math.max(0, days) : 0;
  return getGranularityLevels(safeDays)[0].value;
}

export function H1GranularityToMIN15(data) {
  const newData = [];
  data?.forEach((item) => {
    const baseDateTime = new Date(item?.datetime_timestamp);
    for (let i = 0; i < 4; i += 1) {
      const newItem = { ...item };
      newItem.datetime_timestamp = DateTime.fromJSDate(new Date(baseDateTime))
        .plus({ minutes: i * 15 })
        .toISO();
      newData.push(newItem);
    }
  });
  return newData;
}
